import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaDownload, FaMapMarkerAlt, FaExternalLinkAlt, FaSync,
  FaTable, FaTh, FaTint, FaTimes, FaPlus, FaTrash
} from 'react-icons/fa';
import axios from '../utils/axiosConfig';
import TableView from './TableView';
import PrinterCard from './PrinterCard';
import AddPrinterModal from './AddPrinterModal';
import './AssetsView.css';
import './PrintersView.css';

const MARCA_OPTIONS = ['HP', 'Canon', 'Kyocera'];
const PROVEEDOR_OPTIONS = ['DELCOP', 'SYSCOPY', 'CORP RZ'];

// Celda editable genérica para texto
const EditableTextCell = ({ value, onSave, type = 'text', options = null, placeholder = '' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);

  useEffect(() => { setCurrentValue(value); }, [value]);

  const handleSave = async (newVal) => {
    const success = await onSave(newVal);
    if (!success) setCurrentValue(value);
    setIsEditing(false);
  };

  if (isEditing) {
    if (options) {
      return (
        <select
          value={currentValue || ''}
          onChange={e => handleSave(e.target.value)}
          onBlur={() => setIsEditing(false)}
          autoFocus
          className="w-full px-2 py-1 text-sm border border-sura-blue rounded focus:outline-none focus:ring-2 focus:ring-sura-blue/20"
        >
          <option value="">Seleccionar...</option>
          {options.map(opt => (
            <option key={opt.value ?? opt} value={opt.value ?? opt}>
              {opt.label ?? opt}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        type={type}
        value={currentValue || ''}
        onChange={e => setCurrentValue(e.target.value)}
        onBlur={() => handleSave(currentValue)}
        onKeyDown={e => {
          if (e.key === 'Enter') handleSave(currentValue);
          if (e.key === 'Escape') { setCurrentValue(value); setIsEditing(false); }
        }}
        autoFocus
        placeholder={placeholder}
        className="w-full px-2 py-1 text-sm border border-sura-blue rounded focus:outline-none focus:ring-2 focus:ring-sura-blue/20"
      />
    );
  }

  const displayValue = type === 'number' && value
    ? Number(value).toLocaleString('es-VE')
    : (value || '—');

  return (
    <span
      onDoubleClick={() => setIsEditing(true)}
      className="editable-cell"
      title="Doble click para editar"
      style={type === 'number' ? { fontVariantNumeric: 'tabular-nums' } : undefined}
    >
      {displayValue}
    </span>
  );
};

// Popover de consumibles
const SuppliesPopover = ({ ip, onClose }) => {
  const [supplies, setSupplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const fetchSupplies = async () => {
      try {
        const response = await axios.get(`/api/impresoras/get-supplies/${ip}`, { timeout: 30000 });
        if (response.data.success) {
          setSupplies(response.data.supplies);
        } else {
          setError(response.data.error || 'SNMP no disponible');
        }
      } catch (err) {
        setError(err.code === 'ECONNABORTED' ? 'Timeout' : 'SNMP no disponible');
      } finally {
        setLoading(false);
      }
    };
    fetchSupplies();
  }, [ip]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const detectInkColor = (name) => {
    if (!name) return null;
    const l = name.toLowerCase();
    if (l.includes('cyan') || l.includes('cian')) return '#00BCD4';
    if (l.includes('magenta')) return '#E91E63';
    if (l.includes('yellow') || l.includes('amarillo')) return '#FFC107';
    if (l.includes('black') || l.includes('negro')) return '#424242';
    return '#78909C';
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95, y: -4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -4 }}
      className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 w-72 overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <FaTint className="text-sura-blue" aria-hidden="true" />
          <span className="text-sm font-semibold text-gray-700">Consumibles</span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded-md transition-colors" aria-label="Cerrar">
          <FaTimes size={12} className="text-gray-400" aria-hidden="true" />
        </button>
      </div>
      <div className="p-3 space-y-2.5 max-h-64 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-6">
            <FaSync className="animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">Consultando…</span>
          </div>
        )}
        {error && <div className="text-sm text-gray-500 text-center py-4">{error}</div>}
        {!loading && !error && supplies.length === 0 && (
          <div className="text-sm text-gray-400 text-center py-4">Sin datos SNMP</div>
        )}
        {!loading && !error && supplies.map((supply, i) => {
          const color = detectInkColor(supply.name);
          const isAvailable = supply.percentage !== null && supply.percentage !== undefined;
          const pct = supply.percentage || 0;

          return (
            <div key={i}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-600">{supply.name}</span>
                <span className="text-xs font-bold" style={{ color: isAvailable ? (pct < 10 ? '#EF4444' : pct < 20 ? '#F59E0B' : '#10B981') : '#9CA3AF' }}>
                  {isAvailable ? `${pct}%` : 'N/D'}
                </span>
              </div>
              {isAvailable ? (
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-[width] duration-500"
                    style={{ width: `${pct}%`, backgroundColor: color }}
                  />
                </div>
              ) : (
                <div className="text-[10px] text-gray-400 italic">Cartucho no original</div>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

// Celda de acciones por fila
const PrinterActionsCell = ({ row, onGetCounter, onDelete }) => {
  const [syncing, setSyncing] = useState(false);
  const [showSupplies, setShowSupplies] = useState(false);

  return (
    <div className="action-buttons">
      {row.original.ip && (
        <>
          <button
            className={`action-button view-button ${syncing ? '' : ''}`}
            onClick={async () => {
              setSyncing(true);
              await onGetCounter(row.original.ip, row.original.id);
              setTimeout(() => setSyncing(false), 800);
            }}
            disabled={syncing}
            aria-label="Sincronizar contador"
            title="Sincronizar contador"
          >
            <FaSync className={`text-[10px] ${syncing ? 'animate-spin' : ''}`} aria-hidden="true" />
          </button>
          <div className="relative">
            <button
              className={`action-button ${showSupplies ? 'text-sura-blue border-sura-blue bg-blue-50' : 'view-button'}`}
              onClick={() => setShowSupplies(!showSupplies)}
              aria-label="Ver consumibles"
              title="Consumibles"
            >
              <FaTint className="text-[10px]" aria-hidden="true" />
            </button>
            <AnimatePresence>
              {showSupplies && (
                <SuppliesPopover ip={row.original.ip} onClose={() => setShowSupplies(false)} />
              )}
            </AnimatePresence>
          </div>
          <a
            href={row.original.url || `http://${row.original.ip}/`}
            target="_blank"
            rel="noopener noreferrer"
            className="action-button view-button inline-flex items-center justify-center"
            aria-label="Abrir interfaz web"
            title="Interfaz web"
          >
            <FaExternalLinkAlt className="text-[10px]" aria-hidden="true" />
          </a>
        </>
      )}
      <button
        className="action-button delete-button"
        onClick={() => onDelete(row.original.id)}
        aria-label="Eliminar impresora"
        title="Eliminar"
      >
        <FaTrash className="text-[10px]" aria-hidden="true" />
      </button>
    </div>
  );
};

function PrintersView() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [filteredCount, setFilteredCount] = useState(0);
  const [viewMode, setViewMode] = useState('tabla');
  const [showAddModal, setShowAddModal] = useState(false);
  const [sedes, setSedes] = useState([]);

  const [columnVisibility, setColumnVisibility] = useState({
    sede: true,
    nombre: true,
    marca: true,
    modelo: true,
    ip: true,
    serial: true,
    proveedor: true,
    contador: true,
    fechacontador: true,
    acciones: true,
  });

  useEffect(() => {
    fetchData();
    axios.get('/api/sedes').then(res => setSedes(res.data)).catch(() => {});
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/impresoras');
      setData(response.data);
      setFilteredCount(response.data.length);
      setError(null);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(`Error cargando datos: ${err.message}`);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = useCallback(async (id, field, value) => {
    try {
      const response = await axios.patch(`/api/impresoras/${id}`, { [field]: value });
      if (response.data.message) {
        setData(prev => prev.map(item =>
          item.id === id ? { ...item, [field]: value, ...response.data.updated_fields } : item
        ));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error in handleSave:', err);
      return false;
    }
  }, []);

  const handleGetCounter = useCallback(async (ip, printerId) => {
    try {
      const response = await axios.get(`/api/impresoras/get-counter/${ip}`);
      if (response.data.contador) {
        setData(prev => prev.map(item =>
          item.id === printerId
            ? { ...item, contador: response.data.contador, fechacontador: response.data.fechacontador }
            : item
        ));
      }
    } catch (err) {
      console.error('Error getting counter:', err);
    }
  }, []);

  const handleAddPrinter = useCallback((newPrinter) => {
    setData(prev => [...prev, newPrinter]);
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta impresora?')) return;
    try {
      await axios.delete(`/api/impresoras/${id}`);
      setData(prev => prev.filter(item => item.id !== id));
    } catch (err) {
      console.error('Error deleting printer:', err);
    }
  }, []);

  // Color por hash
  const stringToColor = useCallback((str) => {
    if (!str) return { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    const saturation = 65 + (Math.abs(hash >> 8) % 20);
    const lightness = 85 + (Math.abs(hash >> 16) % 10);
    return {
      bg: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
      color: `hsl(${hue}, ${saturation + 20}%, 30%)`,
      border: `hsl(${hue}, ${saturation}%, ${lightness - 15}%)`
    };
  }, []);

  const sedeOptions = useMemo(() =>
    sedes.map(s => ({ value: s.nombre, label: s.nombre })),
    [sedes]
  );

  const handleSedeInlineSave = useCallback(async (id, sedeName) => {
    const sede = sedes.find(s => s.nombre === sedeName);
    if (!sede && sedeName) return false;
    try {
      const response = await axios.patch(`/api/impresoras/${id}`, { sede_id: sede ? sede.id : null });
      if (response.data.message) {
        setData(prev => prev.map(item =>
          item.id === id ? { ...item, sede_id: sede ? sede.id : null, sede: sedeName || null } : item
        ));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error saving sede:', err);
      return false;
    }
  }, [sedes]);

  // Columnas para TableView
  const columns = useMemo(() => [
    {
      header: "Sede",
      accessorKey: "sede",
      size: 120,
      cell: ({ row }) => (
        <EditableTextCell
          value={row.original.sede}
          options={sedeOptions}
          onSave={(val) => handleSedeInlineSave(row.original.id, val)}
        />
      ),
    },
    {
      header: "Nombre",
      accessorKey: "nombre",
      size: 160,
      cell: ({ row }) => (
        <EditableTextCell
          value={row.original.nombre}
          onSave={(val) => handleSave(row.original.id, 'nombre', val)}
        />
      ),
    },
    {
      header: "Marca",
      accessorKey: "marca",
      size: 100,
      cell: ({ row }) => (
        <EditableTextCell
          value={row.original.marca}
          options={MARCA_OPTIONS}
          onSave={(val) => handleSave(row.original.id, 'marca', val)}
        />
      ),
    },
    {
      header: "Modelo",
      accessorKey: "modelo",
      size: 140,
      cell: ({ row }) => (
        <EditableTextCell
          value={row.original.modelo}
          onSave={(val) => handleSave(row.original.id, 'modelo', val)}
        />
      ),
    },
    {
      header: "IP",
      accessorKey: "ip",
      size: 130,
      cell: ({ row }) => (
        <EditableTextCell
          value={row.original.ip}
          onSave={(val) => handleSave(row.original.id, 'ip', val)}
          placeholder="192.168.x.x"
        />
      ),
    },
    {
      header: "Serial",
      accessorKey: "serial",
      size: 140,
      cell: ({ row }) => (
        <EditableTextCell
          value={row.original.serial}
          onSave={(val) => handleSave(row.original.id, 'serial', val)}
        />
      ),
    },
    {
      header: "Proveedor",
      accessorKey: "proveedor",
      size: 120,
      cell: ({ row }) => (
        <EditableTextCell
          value={row.original.proveedor}
          options={PROVEEDOR_OPTIONS}
          onSave={(val) => handleSave(row.original.id, 'proveedor', val)}
        />
      ),
    },
    {
      header: "Contador",
      accessorKey: "contador",
      size: 120,
      cell: ({ getValue }) => {
        const val = getValue();
        return (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {val ? Number(val).toLocaleString('es-VE') : '—'}
          </span>
        );
      },
    },
    {
      header: "Actualizado",
      accessorKey: "fechacontador",
      size: 120,
      cell: ({ getValue }) => {
        const fecha = getValue();
        if (!fecha) return <span className="text-gray-400 italic text-sm">—</span>;
        return (
          <span className="text-sm text-gray-600">
            {new Date(fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        );
      },
    },
    {
      header: "Acciones",
      accessorKey: "acciones",
      id: "acciones",
      size: 140,
      maxSize: 140,
      enableSorting: false,
      cell: ({ row }) => (
        <PrinterActionsCell row={row} onGetCounter={handleGetCounter} onDelete={handleDelete} />
      ),
    },
  ], [stringToColor, handleSave, handleGetCounter, handleDelete, sedeOptions, handleSedeInlineSave]);

  // Vista de tarjetas: agrupadas por sede
  const groupedData = useMemo(() => {
    return data.reduce((acc, printer) => {
      const sede = printer.sede || 'Sin Sede';
      if (!acc[sede]) acc[sede] = [];
      acc[sede].push(printer);
      return acc;
    }, {});
  }, [data]);

  const sortSedes = (entries) => {
    const sedeOrder = { 'CA1': 1, 'CDN': 2, 'CCS': 3 };
    return entries.sort(([a], [b]) => {
      const oA = sedeOrder[a] || 999;
      const oB = sedeOrder[b] || 999;
      return oA !== oB ? oA - oB : a.localeCompare(b);
    });
  };

  const handleExportToCSV = () => {
    try {
      const exportColumns = [
        { key: 'id', header: 'ID' },
        { key: 'sede', header: 'Sede' },
        { key: 'marca', header: 'Marca' },
        { key: 'modelo', header: 'Modelo' },
        { key: 'nombre', header: 'Nombre' },
        { key: 'serial', header: 'Serial' },
        { key: 'ip', header: 'IP' },
        { key: 'proveedor', header: 'Proveedor' },
        { key: 'contador', header: 'Contador' },
        { key: 'fechacontador', header: 'Fecha Contador' }
      ];

      let csvContent = exportColumns.map(col => `"${col.header}"`).join(',') + '\n';
      data.forEach(row => {
        const rowData = exportColumns.map(col => {
          let value = row[col.key] !== null && row[col.key] !== undefined ? row[col.key] : '';
          return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvContent += rowData.join(',') + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'Impresoras.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error al exportar a CSV:", err);
    }
  };

  // Filtros para TableView
  const availableFilters = useMemo(() => [
    { id: 'sede', placeholder: 'Todas las sedes' },
    { id: 'marca', placeholder: 'Todas las marcas' },
    { id: 'proveedor', placeholder: 'Todos los proveedores' },
  ], []);

  return (
    <div className="assets-view printers-view">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
        <h2 className="page-title">Impresoras ({filteredCount})</h2>

        <div className="flex items-center gap-3">
          <button
            className="btn-primary"
            onClick={() => setShowAddModal(true)}
            aria-label="Nueva Impresora"
          >
            <FaPlus aria-hidden="true" /> Nueva Impresora
          </button>
          <button
            className="btn-secondary"
            onClick={handleExportToCSV}
            disabled={loading || data.length === 0}
            aria-label="Exportar a Excel"
          >
            <FaDownload aria-hidden="true" /> Exportar
          </button>

          {/* Toggle de vista */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('tabla')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'tabla' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              aria-label="Vista tabla"
            >
              <FaTable size={12} aria-hidden="true" />
              Tabla
            </button>
            <button
              onClick={() => setViewMode('tarjetas')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'tarjetas' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              aria-label="Vista tarjetas"
            >
              <FaTh size={12} aria-hidden="true" />
              Tarjetas
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {viewMode === 'tabla' ? (
          <motion.div
            key="tabla"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <TableView
              data={data}
              columns={columns}
              loading={loading}
              error={error}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              defaultPageSize={30}
              defaultSorting={[{ id: 'sede', desc: false }]}
              activeActionMenu={activeActionMenu}
              setActiveActionMenu={setActiveActionMenu}
              availableFilters={availableFilters}
              onFilteredCountChange={setFilteredCount}
            />
          </motion.div>
        ) : (
          <motion.div
            key="tarjetas"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <div className="table-container">
              <div className="card-view-container">
                {loading && (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                    <div className="w-12 h-12 rounded-full border-3 border-gray-200 border-t-gray-600 animate-spin" />
                    <p className="mt-4 text-sm font-medium text-shimmer">Cargando impresoras…</p>
                  </div>
                )}
                {error && <p>{error}</p>}
                {!loading && !error && sortSedes(Object.entries(groupedData)).map(([sede, printers], sedeIndex) => {
                  return (
                    <div key={sede} className="sede-group animate-fadeIn" style={{ animationDelay: `${sedeIndex * 0.1}s` }}>
                      <div className="sede-header">
                        <FaMapMarkerAlt className="sede-header-icon" aria-hidden="true" />
                        <h3 className="sede-title">
                          {sede}
                          <span className="sede-count">{printers.length}</span>
                        </h3>
                      </div>
                      <div className="sede-printers">
                        {printers.map((printer, index) => (
                          <div key={printer.id} className="animate-fadeIn" style={{ animationDelay: `${index * 0.04}s` }}>
                            <PrinterCard printer={printer} onSave={handleSave} onGetCounter={handleGetCounter} />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AddPrinterModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddPrinter}
      />
    </div>
  );
}

export default PrintersView;
